/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/pages/Cockpit.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { Wallet, TrendingUp, Receipt, Banknote, Bot, ListChecks, Landmark, ArrowRight, CalendarClock, CircleCheck, Lock } from 'lucide-react';
import { useApi } from '../lib/hooks';
import { navigate } from '../lib/router';
import { useLang } from '../lib/i18n';
import { I, Badge, ErrorCard, LoadingRows, Skeleton, Empty } from '../components/ui';
import { BarChart } from '../components/BarChart';
import { PulseCard } from '../components/PulseCard';
import { StatusBadge } from '../components/AgentCards';
import { chf, date, pct, relTime } from '../lib/format';

export function Cockpit({ tid }: { tid: string }) {
  const { t } = useLang();
  const o = useApi<any>(`/api/t/${tid}/overview`);
  const dl = useApi<any[]>(`/api/t/${tid}/deadlines`);
  const props = useApi<any[]>(`/api/t/${tid}/proposals`);
  const d = o.data;
  const go = (p: string) => navigate(`/t/${tid}/${p}`);

  if (o.error) return <ErrorCard message={o.error} onRetry={o.reload} />;
  const kpis = d ? [
    { icon: Wallet, label: t('cockpit.liquidity'), value: chf(d.cash), hint: t('cockpit.liquidity_hint') },
    { icon: TrendingUp, label: t('cockpit.result', { year: d.today.slice(0, 4) }), value: chf(d.result), hint: t('cockpit.result_hint', { rev: chf(d.revenue, { currency: false }), exp: chf(d.expenses, { currency: false }) }), neg: d.result < 0 },
    { icon: Receipt, label: t('cockpit.vat', { period: d.vat.period }), value: chf(d.vat.payable), hint: d.vat.filed ? (d.vat.submitted ? t('cockpit.vat_filed') : t('cockpit.vat_filed_unsubmitted')) : `${d.vat.payable >= 0 ? t('cockpit.vat_payable') : t('cockpit.vat_credit')}, ${t('cockpit.vat_due', { date: date(d.vat.dueDate) })}` },
    { icon: Banknote, label: t('cockpit.open_receivables'), value: chf(d.openReceivables.s), hint: d.overdue.n ? t('cockpit.receivables_overdue', { n: d.overdue.n, amount: chf(d.overdue.s) }) : t('cockpit.receivables_none_overdue', { n: d.openReceivables.n }) },
    { icon: Banknote, label: t('cockpit.open_payables'), value: chf(d.openPayables.s), hint: t('cockpit.payables_for_payment', { n: d.openPayables.n }) },
    { icon: Bot, label: t('cockpit.automation'), value: pct(d.autonomy.rate), hint: t('cockpit.automation_hint', { auto: d.autonomy.autoPosted, decided: d.autonomy.decided }) },
  ] : [];
  const todo = d ? d.pending + d.questions + d.unmatched : 0;

  return (
    <>
      <div className="page-head">
        <div><h2>{d?.tenant.name ?? <Skeleton w={260} h={24} />}</h2><p>{t('cockpit.as_of', { date: d ? date(d.today) : '…' })} · {d?.tenant.vatMethod === 'saldo' ? t('cockpit.vat_saldo') : t('cockpit.vat_effective')}{d?.tenant.lockedUntil && <> · <I icon={Lock} sm /> {t('cockpit.lock_until', { date: date(d.tenant.lockedUntil) })}</>}</p></div>
        <div className="actions">
          <button className="btn" onClick={() => go('agent-office')}>Agenten-Büro (Live)</button>
          <button className="btn" onClick={() => go('reports')}>{t('cockpit.open_balance')}</button>
          <button className="btn primary" onClick={() => go('agent')}><I icon={Bot} sm /> {t('cockpit.ask_agent')}</button>
        </div>
      </div>

      <div className="grid kpis" data-copy-surface>
        {!d && Array.from({ length: 6 }, (_, i) => <div key={i} className="card kpi"><Skeleton w="50%" h={12} /><Skeleton w="70%" h={24} /><Skeleton w="80%" h={10} /></div>)}
        {kpis.map((k) => (
          <div key={k.label} className="card kpi" data-copy-unit>
            <span className="label"><I icon={k.icon} sm />{k.label}</span>
            <span className={`value num ${k.neg ? 'neg' : ''}`}>{k.value}</span>
            <span className="hint">{k.hint}</span>
          </div>
        ))}
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 26rem), 1fr))' }}>
        <PulseCard tid={tid} />

        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-head"><h3>{t('cockpit.monthly_chart')}</h3><span className="sub">{t('cockpit.monthly_sub', { year: d?.today.slice(0, 4) ?? '' })}</span></div>
          <div className="card-pad">{d ? (d.monthly.length ? <BarChart data={d.monthly} /> : <Empty icon={TrendingUp} title={t('ui.empty')}>Sobald Belege verbucht sind, erscheint hier der Verlauf.</Empty>) : <Skeleton h={220} />}</div>
        </div>

        <div className="card">
          <div className="card-head"><h3>{t('cockpit.waiting_for_you')}</h3>{d && <Badge kind={todo ? 'warn' : 'pos'} dot>{todo ? `${todo} ${t('ui.open')}` : t('ui.all_done')}</Badge>}</div>
          <div className="side-list">
            {[
              { n: d?.pending, icon: ListChecks, t: t('cockpit.proposals'), s: t('cockpit.proposals_hint'), p: 'review' },
              { n: d?.questions, icon: Bot, t: t('cockpit.client_questions'), s: t('cockpit.client_questions_hint'), p: 'agent' },
              { n: d?.unmatched, icon: Landmark, t: t('cockpit.unmatched_bank'), s: t('cockpit.unmatched_bank_hint'), p: 'bank' },
            ].map((x) => (
              <button key={x.p} className="side-item" style={{ background: 'none', border: 0, borderBottom: '1px solid var(--border)', textAlign: 'left', cursor: 'pointer', width: '100%' }} onClick={() => go(x.p)}>
                <I icon={x.icon} />
                <span className="t"><b>{x.t}</b><span>{x.s}</span></span>
                <Badge kind={x.n ? 'accent' : undefined}>{x.n ?? '…'}</Badge>
              </button>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>{t('cockpit.deadlines')}</h3><span className="sub">{t('cockpit.deadlines_sub')}</span></div>
          {dl.loading && !dl.data ? <LoadingRows rows={4} /> : (
            <div className="side-list" data-copy-surface>
              {(dl.data ?? []).filter((x) => !x.done).slice(0, 5).map((x) => (
                <div key={x.title} className="side-item" data-copy-unit>
                  <I icon={x.done ? CircleCheck : CalendarClock} />
                  <span className="t"><b>{x.title}</b><span>{x.detail}</span></span>
                  <span className={`num ${x.date < d?.today ? 'neg' : 'muted'}`} style={{ fontSize: 'var(--fs-sm)', flex: 'none' }}>{x.date < d?.today ? t('cockpit.overdue_prefix') : ''}{date(x.date)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-head"><h3>{t('cockpit.recent_decisions')}</h3><button className="btn ghost sm" onClick={() => go('journal')}>{t('cockpit.journal_link')} <I icon={ArrowRight} sm /></button></div>
          {props.loading && !props.data ? <LoadingRows rows={5} /> : (
            <div className="table-wrap">
              <table className="data compact" data-copy-surface>
                <thead><tr><th>{t('cockpit.col_receipt')}</th><th className="r">{t('cockpit.col_amount')}</th><th>{t('cockpit.col_confidence')}</th><th>{t('cockpit.col_status')}</th><th className="r">{t('cockpit.col_when')}</th></tr></thead>
                <tbody>
                  {(props.data ?? []).slice(0, 7).map((p) => (
                    <tr key={p.id}>
                      <td>{p.card.description}<span className="sub">{p.card.lines.map((l: any) => l.account).join(' / ')}</span></td>
                      <td className="r num">{chf(p.card.amount, { currency: false })}</td>
                      <td className="num">{pct(p.confidence)}</td>
                      <td><StatusBadge status={p.status} /></td>
                      <td className="r dim">{relTime(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
